import React from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { useNavigate } from 'react-router-dom';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Alert from '@mui/material/Alert';
import { sendSupportTicket } from '../api/support';
import InfoModal from '../components/InfoModal';

function NotQualified() {
    const [openSupport, setOpenSupport] = React.useState(false);
    const [firstName, setFirstName] = React.useState('');
    const [lastName, setLastName] = React.useState('');
    const [schoolName, setSchoolName] = React.useState('');
    const [email, setEmail] = React.useState('');
    const [supportType, setSupportType] = React.useState('');
    const [supportReason, setSupportReason] = React.useState('');
    const [supportLoading, setSupportLoading] = React.useState(false);
    const [snackbar, setSnackbar] = React.useState({ open: false, severity: 'success', message: '' });
    const [infoOpen, setInfoOpen] = React.useState(false);
    const [infoQuestion, setInfoQuestion] = React.useState('');
    const navigate = useNavigate();

    const handleOpenSupport = () => {
        setSnackbar({ open: false, severity: 'success', message: '' });
        setSupportReason('');
        setSupportType('');
        setFirstName('');
        setLastName('');
        setSchoolName('');
        setEmail('');
        setOpenSupport(true);
    };
    const handleCloseSupport = () => {
        setOpenSupport(false);
    };
    const handleOpenInfo = (question) => {
        setInfoQuestion(question || '');
        setInfoOpen(true);
    };
    const handleCloseInfo = () => {
        setInfoOpen(false);
        setInfoQuestion('');
    };
    const handleSubmitSupport = async () => {
        setSupportLoading(true);
        try {
            await sendSupportTicket({ firstName, lastName, schoolName, email, supportType, additionalInformation: supportReason });
            setSnackbar({ open: true, severity: 'success', message: 'Support request sent!' });
            setTimeout(() => {
                setSnackbar({ open: false, severity: 'success', message: '' });
                setOpenSupport(false);
            }, 1500);
        } catch (err) {
            setSnackbar({ open: true, severity: 'error', message: err?.response?.data?.message || 'Failed to send support request' });
        } finally {
            setSupportLoading(false);
        }
    };

    return (
        <Box component="main">
            <Grid container justifyContent="center">
                <Grid item xs={12} md={8} lg={6}>
                    <Paper elevation={1} sx={{ p: 0, overflow: 'hidden' }}>
                        <Box sx={{ bgcolor: 'black', display: 'flex', alignItems: 'center', justifyContent: 'flex-start', px: 3, py: { xs: 1.5, md: 2 } }}>
                            <Box
                                component="img"
                                src="ryslogo.png"
                                alt="Retool Your School"
                                sx={{ height: { xs: 42, md: 60 }, objectFit: 'contain' }}
                            />
                        </Box>
                        <Box sx={{ p: { xs: 3, md: 4 } }}>
                            <Typography sx={{ fontSize: '24px', fontWeight: 700, mb: 2 }}>
                                You are not eligible to proceed.
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 2 }}>
                                Thank you for your interest. You must be a School Administrator and/or Faculty member of an institution with a current{' '}
                                <Typography component="span" sx={{ color: 'primary.main', textDecoration: 'underline', cursor: 'pointer', fontWeight: 700 }} onClick={() => handleOpenInfo('What are the specific eligibility requirements?')}>
                                    student enrollment under 16,000
                                </Typography>
                                {' '}to proceed. For more information on eligibility requirements, please review the{' '}
                                <Typography component="span" sx={{ color: 'primary.main', textDecoration: 'underline', cursor: 'pointer', fontWeight: 700 }} onClick={() => handleOpenInfo()}>
                                    FAQ
                                </Typography>
                                .
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 2 }}>
                                If you believe this is an error, please contact support for assistance.
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <Button variant="contained" onClick={handleOpenSupport}>
                                    Contact Support
                                </Button>
                                <Button variant="outlined" onClick={() => navigate('/')}>
                                    Back To Eligibility Check
                                </Button>
                            </Box>
                        </Box>
                    </Paper>
                </Grid>
            </Grid>

            <Dialog open={openSupport} onClose={handleCloseSupport} fullWidth maxWidth="sm">
                <DialogTitle>Support Request</DialogTitle>
                <DialogContent>
                    {snackbar.open && (
                        <Alert severity={snackbar.severity} sx={{ mb: 2 }}>
                            {snackbar.message}
                        </Alert>
                    )}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <TextField label="First name" value={firstName} onChange={(e) => setFirstName(e.target.value)} fullWidth required InputLabelProps={{ sx: { '& .MuiFormLabel-asterisk': { color: '#f57c00' } } }} />
                        <TextField label="Last name" value={lastName} onChange={(e) => setLastName(e.target.value)} fullWidth required InputLabelProps={{ sx: { '& .MuiFormLabel-asterisk': { color: '#f57c00' } } }} />
                        <TextField label="School name" value={schoolName} onChange={(e) => setSchoolName(e.target.value)} fullWidth required InputLabelProps={{ sx: { '& .MuiFormLabel-asterisk': { color: '#f57c00' } } }} />
                        <TextField label="Email" value={email} onChange={(e) => setEmail(e.target.value)} fullWidth required InputLabelProps={{ sx: { '& .MuiFormLabel-asterisk': { color: '#f57c00' } } }} />
                        <FormControl fullWidth required>
                            <InputLabel id="support-type-label" sx={{ '& .MuiFormLabel-asterisk': { color: '#f57c00' } }}>Choose your type of support</InputLabel>
                            <Select
                                labelId="support-type-label"
                                value={supportType}
                                label={"Choose your type of support"}
                                onChange={(e) => setSupportType(e.target.value)}
                            >
                                <MenuItem value={"My school is not eligible"}>My school is not eligible</MenuItem>
                                <MenuItem value={"I'm having trouble with my application"}>I'm having trouble with my application</MenuItem>
                                <MenuItem value={"I'm having trouble inviting a collaborator"}>I'm having trouble inviting a collaborator</MenuItem>
                                <MenuItem value={"I didn't receive my email verification"}>I didn't receive my email verification</MenuItem>
                                <MenuItem value={"Other"}>Other</MenuItem>
                            </Select>
                        </FormControl>
                        <TextField label="Additional information" value={supportReason} onChange={e => setSupportReason(e.target.value)} fullWidth multiline minRows={3} required InputLabelProps={{ sx: { '& .MuiFormLabel-asterisk': { color: '#f57c00' } } }} />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseSupport} disabled={supportLoading}>Cancel</Button>
                    <Button onClick={handleSubmitSupport} variant="contained" disabled={supportLoading || !supportReason.trim() || !firstName.trim() || !lastName.trim() || !schoolName.trim() || !email.trim() || !supportType}>
                        {supportLoading ? 'Sending...' : 'Send'}
                    </Button>
                </DialogActions>
            </Dialog>
            <InfoModal open={infoOpen} onClose={handleCloseInfo} openQuestion={infoQuestion} />
        </Box>
    );
}

export default NotQualified;
